import { Box } from "@mui/material";
import { isNil } from "lodash-es";

import PageTabContent from "@/App/Layouts/PageBody/PageTabContent";
import PageTabHeader from "@/App/Layouts/PageBody/PageTabHeader";
import ViewContentBlock from "@/App/Layouts/ViewContentBlock";
import { ViewLayout } from "@/App/Layouts/ViewLayout";
import Datetime from "@/common/components/Datetime/Datetime";
import AccessoryCheckItem from "@/common/components/Entity/AccessoryCheck/AccessoryCheckItem";
import GeneralInspectorDisplay from "@/common/components/Entity/General/Display/GeneralInspectorDisplay";
import EntityChangedByInfoCard from "@/common/components/EntityInfo/EntityChangedByInfoCard";
import FileListView from "@/common/components/Files/FileListView";
import InfoTile from "@/common/components/Form/Display/InfoTile";
import BaseEditExternalNumberModal from "@/common/components/Modals/common/BaseEditExternalNumberModal";
import BaseEditNotesModal from "@/common/components/Modals/common/BaseEditNotesModal";
import { ATTACHMENTS_COMMON_ITEM_SIZES } from "@/common/constants/common";
import { FileItem } from "@/common/fileItem";
import { ArrayHelper } from "@/common/helpers/array";
import { ViewVariant } from "@/common/ts/layout";
import { apiClient } from "@/core/api/ApiClient";
import { AccessoryCheckDto, AppPermission, EntityType } from "@/core/api/generated/v1";

import ContractLink from "../../../Contract/ContractLink";
import EntityAffiliationInfoBlock from "../../../EntityAffiliation/EntityAffiliationInfoBlock";
import VehicleLink from "../../../Vehicle/VehicleLink";

interface Props {
  accessoryCheck: AccessoryCheckDto;
  onUpdate: (newValue?: AccessoryCheckDto) => void;
}

export default function OverviewTabContent({ accessoryCheck, onUpdate }: Props) {
  return (
    <PageTabContent>
      <ViewLayout
        displayProps={{ viewVariant: ViewVariant.Tab }}
        header={
          <PageTabHeader
            title={"Details"}
            subtitle2={<EntityChangedByInfoCard entity={accessoryCheck} />}
          />
        }
      >
        <ViewContentBlock>
          {/* {(accessoryCheck.vehicle || accessoryCheck.contract) && (
            <Stack direction={{ xxs: "column", md: "row" }} alignItems='flex-start' spacing={2}>
              {accessoryCheck.vehicle && (
                <VehicleBlock
                  sx={{ xxs: "100%", maxWidth: { md: 400 } }}
                  variant='reference'
                  vehicle={accessoryCheck.vehicle}
                  withDetailsToggle
                  withLink
                />
              )}

              {accessoryCheck.contract && (
                <ContractBlock
                  sx={{ maxWidth: { xxs: "100%", md: 400 } }}
                  variant='reference'
                  contract={accessoryCheck.contract}
                  withDetailsToggle
                  withLink
                />
              )}
            </Stack>
          )} */}

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xxs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gridTemplateRows: "auto",
              rowGap: 1,
              columnGap: 1,
            }}
          >
            {/* <InfoTile
              label='Number'
              iconOf='localNumber'
              isEmpty={isNil(accessoryCheck?.localNumber)}
            >
              {accessoryCheck?.localNumber}
            </InfoTile> */}

            <InfoTile label='Vehicle' iconOf='vehicle' isEmpty={isNil(accessoryCheck?.vehicle)}>
              <VehicleLink entity={accessoryCheck?.vehicle} />
            </InfoTile>

            <InfoTile label='Contract' iconOf='contract' isEmpty={isNil(accessoryCheck?.contract)}>
              <ContractLink entity={accessoryCheck?.contract} />
            </InfoTile>

            <InfoTile
              label='External number'
              iconOf='externalNumber'
              isEmpty={!accessoryCheck.externalNumber}
              endAdornment={
                <BaseEditExternalNumberModal
                  initialValues={{ externalNumber: accessoryCheck.externalNumber }}
                  onConfirm={async (externalNumber) => {
                    await apiClient.accessoryChecksApi.apiV1AccessoryChecksAccessoryCheckIdExternalNumberPut(
                      {
                        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                        accessoryCheckId: accessoryCheck.id || "",
                        generalExternalNumberInputDto: { externalNumber },
                      },
                    );
                    onUpdate();
                  }}
                />
              }
            >
              {accessoryCheck.externalNumber}
            </InfoTile>
          </Box>

          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: {
                xxs: "1fr",
                sm: "repeat(2, 1fr)",
                md: "repeat(4, 1fr)",
              },
              gridTemplateRows: "auto",
              rowGap: 1,
              columnGap: 1,
            }}
          >
            <InfoTile
              label='Inspected at'
              iconOf='date'
              isEmpty={isNil(accessoryCheck?.inspectedAt)}
            >
              <Datetime datetime={accessoryCheck?.inspectedAt} />
            </InfoTile>

            <InfoTile
              label='Inspector'
              iconOf='inspector'
              isEmpty={isNil(accessoryCheck?.inspector)}
            >
              <GeneralInspectorDisplay
                inspector={accessoryCheck?.inspector}
                inlineUserProps={{ withLink: true, withAvatar: true, withPoper: true }}
              />
            </InfoTile>

            <InfoTile
              label='Notes'
              iconOf='notes'
              isEmpty={!accessoryCheck.notes}
              endAdornment={
                <BaseEditNotesModal
                  initialValues={{ notes: accessoryCheck.notes }}
                  onConfirm={async (notes) => {
                    await apiClient.accessoryChecksApi.apiV1AccessoryChecksAccessoryCheckIdNotesPut(
                      {
                        nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
                        accessoryCheckId: accessoryCheck.id || "",
                        generalNotesInputDto: { notes },
                      },
                    );
                    onUpdate();
                  }}
                />
              }
            >
              {accessoryCheck.notes}
            </InfoTile>
          </Box>
        </ViewContentBlock>

        {/* Affiliation */}
        <EntityAffiliationInfoBlock
          entityType={EntityType.AccessoryCheck}
          entityId={accessoryCheck.id}
          entity={accessoryCheck}
          entityUpdatePermission={AppPermission.AccessoryCheckManage}
          infoDisplayProps={{
            withoutTenant: true,
          }}
          onUpdate={onUpdate}
        />

        {!ArrayHelper.isEmpty(accessoryCheck.attachments) && (
          <ViewContentBlock>
            <FileListView
              files={FileItem.createManyFrom(accessoryCheck.attachments)}
              maxVisibleFiles={10}
              itemWidth={ATTACHMENTS_COMMON_ITEM_SIZES.WIDTH}
              itemHeight={ATTACHMENTS_COMMON_ITEM_SIZES.HEIGHT}
            />
          </ViewContentBlock>
        )}

        {accessoryCheck.items?.length !== 0 && (
          <ViewContentBlock>
            {accessoryCheck.items?.map((item) => (
              <AccessoryCheckItem key={item.id} item={item} />
            ))}
          </ViewContentBlock>
        )}
      </ViewLayout>
    </PageTabContent>
  );
}
